import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Users, Car, FileText, Wrench, DollarSign, Clock, AlertCircle, CheckCircle, LogIn, CreditCard, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fetchDashboardStats, fetchInvoices, fetchFieldServices, fetchContracts, fetchCustomers, fetchResellerProfile, initiateStripeOnboarding, AuthenticationError } from "@/lib/reseller-api";
import { fetchDevices, fetchPermissionsMap, userDevicesFromMap } from "@/lib/traccar-api";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";

const COLORS = {
  primary: "hsl(var(--primary))",
  green: "#22c55e",
  yellow: "#eab308",
  red: "#ef4444",
  blue: "#3b82f6",
  gray: "#6b7280",
};

export default function ResellerDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stripeLoading, setStripeLoading] = useState(false);
  const { toast } = useToast();

  const { data: profile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: fetchResellerProfile,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) return false;
      return failureCount < 3;
    },
  });

  const stripeOnboardMutation = useMutation({
    mutationFn: () => {
      setStripeLoading(true);
      return initiateStripeOnboarding(window.location.origin);
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (error: Error) => {
      setStripeLoading(false);
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const { data: stats, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ["reseller-dashboard"],
    queryFn: fetchDashboardStats,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) return false;
      return failureCount < 3;
    },
  });

  const { data: invoices = [] } = useQuery({
    queryKey: ["invoices"],
    queryFn: fetchInvoices,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) return false;
      return failureCount < 3;
    },
  });

  const { data: services = [] } = useQuery({
    queryKey: ["field-services"],
    queryFn: fetchFieldServices,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) return false;
      return failureCount < 3;
    },
  });

  const { data: contracts = [] } = useQuery({
    queryKey: ["contracts"],
    queryFn: fetchContracts,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) return false;
      return failureCount < 3;
    },
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: fetchCustomers,
    retry: (failureCount, error) => {
      if (error instanceof AuthenticationError) return false;
      return failureCount < 3;
    },
  });

  const traccarUserIds = customers
    .filter((c) => c.traccar_user_id)
    .map((c) => c.traccar_user_id!);

  const { data: allDevices = [] } = useQuery({
    queryKey: ["devices"],
    queryFn: fetchDevices,
  });

  const { data: totalDevices = 0 } = useQuery({
    queryKey: ["total-device-count", traccarUserIds],
    queryFn: async () => {
      const perms = await fetchPermissionsMap();
      return traccarUserIds.reduce(
        (sum, userId) => sum + userDevicesFromMap(userId, allDevices, perms).length,
        0
      );
    },
    enabled: traccarUserIds.length > 0 && allDevices.length > 0,
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  };

  // Invoice status distribution
  const invoiceStatusData = invoices.reduce((acc, inv) => {
    const existing = acc.find((a) => a.name === inv.status);
    if (existing) existing.value++;
    else acc.push({ name: inv.status, value: 1 });
    return acc;
  }, [] as { name: string; value: number }[]);

  const invoiceStatusColors: Record<string, string> = {
    paid: COLORS.green,
    open: COLORS.blue,
    draft: COLORS.gray,
    uncollectible: COLORS.red,
    void: COLORS.gray,
  };

  // Contract status distribution
  const contractStatusData = contracts.reduce((acc, c) => {
    const existing = acc.find((a) => a.name === c.status);
    if (existing) existing.value++;
    else acc.push({ name: c.status, value: 1 });
    return acc;
  }, [] as { name: string; value: number }[]);

  const contractStatusColors: Record<string, string> = {
    active: COLORS.green,
    pending: COLORS.yellow,
    cancelled: COLORS.red,
    expired: COLORS.gray,
  };

  // Monthly revenue (last 6 months) — use created date for paid invoices
  const monthlyRevenue = invoices
    .filter((inv) => inv.status === "paid")
    .reduce((acc, inv) => {
      const month = new Date(inv.created).toLocaleDateString("pt-BR", { month: "short" });
      const existing = acc.find((a) => a.month === month);
      if (existing) existing.revenue += inv.amount;
      else acc.push({ month, revenue: inv.amount });
      return acc;
    }, [] as { month: string; revenue: number }[])
    .slice(-6);

  // Recent invoices
  const recentInvoices = invoices.slice(0, 5);

  // Upcoming services
  const upcomingServices = services
    .filter((s) => s.status === "scheduled" || s.status === "in_progress")
    .slice(0, 5);

  const statusIcon = (status: string) => {
    switch (status) {
      case "paid":
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "overdue":
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case "in_progress":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  // Check for authentication error
  const isAuthError = statsError instanceof AuthenticationError;

  if (isAuthError) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-full">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center space-y-4">
              <LogIn className="h-12 w-12 mx-auto text-muted-foreground" />
              <h2 className="text-xl font-semibold">{t("reseller.authRequired")}</h2>
              <p className="text-muted-foreground">{t("reseller.authMessage")}</p>
              <Button onClick={() => navigate("/login")} className="w-full">
                <LogIn className="h-4 w-4 mr-2" />
                {t("reseller.goToLogin")}
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  if (statsLoading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center h-full">
          <p className="text-muted-foreground">{t("common.loading")}</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Stripe Connect Banner */}
        {profile && !profile.stripe_account_id && (
          <Card className="border-dashed">
            <CardContent className="pt-6 flex items-center justify-between">
              <div>
                <p className="font-medium">{t("reseller.stripe.connectTitle")}</p>
                <p className="text-sm text-muted-foreground">{t("reseller.stripe.connectDescription")}</p>
              </div>
              <Button onClick={() => stripeOnboardMutation.mutate()} disabled={stripeLoading}>
                {stripeLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
                {t("reseller.stripe.connectButton")}
              </Button>
            </CardContent>
          </Card>
        )}
        {profile?.stripe_onboarding_complete && (
          <div className="flex items-center gap-2">
            <Badge className="bg-green-500"><CreditCard className="h-3 w-3 mr-1" /> {t("reseller.stripe.connected")}</Badge>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("reseller.stats.customers")}</p>
                  <p className="text-2xl font-bold">{stats?.customers || 0}</p>
                </div>
                <Users className="h-8 w-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("reseller.stats.vehicles")}</p>
                  <p className="text-2xl font-bold">{totalDevices}</p>
                </div>
                <Car className="h-8 w-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("reseller.stats.activeContracts")}</p>
                  <p className="text-2xl font-bold">{stats?.activeContracts || 0}</p>
                </div>
                <FileText className="h-8 w-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("reseller.stats.pendingServices")}</p>
                  <p className="text-2xl font-bold">{stats?.pendingServices || 0}</p>
                </div>
                <Wrench className="h-8 w-8 text-primary opacity-80" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t("reseller.stats.totalRevenue")}</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats?.totalRevenue || 0)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500 opacity-80" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Invoice Status */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("reseller.charts.invoiceStatus")}</CardTitle>
            </CardHeader>
            <CardContent>
              {invoiceStatusData.length === 0 ? (
                <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">
                  {t("reseller.charts.noInvoices")}
                </div>
              ) : (
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={invoiceStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="value"
                        label={({ name, value }) => `${t(`reseller.invoice.statuses.${name}`)}: ${value}`}
                        labelLine={false}
                      >
                        {invoiceStatusData.map((entry, index) => (
                          <Cell key={index} fill={invoiceStatusColors[entry.name] || COLORS.gray} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, t(`reseller.invoice.statuses.${name}`)]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contract Status */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("reseller.charts.contractStatus")}</CardTitle>
            </CardHeader>
            <CardContent>
              {contractStatusData.length === 0 ? (
                <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">
                  {t("reseller.charts.noContracts")}
                </div>
              ) : (
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={contractStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="value"
                        label={({ name, value }) => `${t(`reseller.contract.statuses.${name}`)}: ${value}`}
                        labelLine={false}
                      >
                        {contractStatusData.map((entry, index) => (
                          <Cell key={index} fill={contractStatusColors[entry.name] || COLORS.gray} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [value, t(`reseller.contract.statuses.${name}`)]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Revenue */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t("reseller.charts.monthlyRevenue")}</CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyRevenue.length === 0 ? (
                <div className="h-[180px] flex items-center justify-center text-muted-foreground text-sm">
                  {t("reseller.charts.noRevenue")}
                </div>
              ) : (
                <div className="h-[180px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyRevenue}>
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${v / 1000}k`} />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Bar dataKey="revenue" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Lists Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Recent Invoices */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t("reseller.lists.recentInvoices")}</CardTitle>
            </CardHeader>
            <CardContent>
              {recentInvoices.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t("reseller.lists.noInvoices")}</p>
              ) : (
                <div className="space-y-3">
                  {recentInvoices.map((invoice) => (
                    <div key={invoice.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {statusIcon(invoice.status)}
                        <div>
                          <p className="font-medium text-sm">{invoice.number || invoice.id.slice(0, 12)}</p>
                          <p className="text-xs text-muted-foreground">{invoice.customer_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-sm">{formatCurrency(invoice.amount)}</p>
                        <Badge variant="outline" className="text-xs">
                          {t(`reseller.invoice.statuses.${invoice.status}`)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Services */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">{t("reseller.lists.upcomingServices")}</CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingServices.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t("reseller.lists.noServices")}</p>
              ) : (
                <div className="space-y-3">
                  {upcomingServices.map((service) => (
                    <div key={service.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {statusIcon(service.status)}
                        <div>
                          <p className="font-medium text-sm">{t(`reseller.fieldService.types.${service.service_type}`)}</p>
                          <p className="text-xs text-muted-foreground">
                            {service.customer?.name}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm">
                          {service.scheduled_date ? formatDate(service.scheduled_date) : t("reseller.common.notScheduled")}
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {t(`reseller.fieldService.statuses.${service.status}`)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
